
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Users, Clock, Eye, Bell, Settings } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';

const ParentalSettings = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  
  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;
  const userProfile = profile || (isInternalUser ? {} : null);

  // Check if user is a parent
  if (!user || (userProfile && userProfile.role !== 'parent')) {
    return <Navigate to="/" replace />;
  }

  // Parental control state
  const [childAccountMonitoring, setChildAccountMonitoring] = useState(true);
  const [messageApproval, setMessageApproval] = useState(true);
  const [teamCommunication, setTeamCommunication] = useState('approval-required');
  const [activityReports, setActivityReports] = useState('weekly');
  const [emergencyNotifications, setEmergencyNotifications] = useState(true);
  const [coachCommunication, setCoachCommunication] = useState(true);
  const [screenTimeLimit, setScreenTimeLimit] = useState('2');

  const handleSaveSettings = () => {
    // Here you would implement the actual saving logic
    toast.success('Parental settings saved successfully');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <MobileHeader />
      
      <main className="pt-20 pb-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline" 
              className="border-gray-600 text-gray-400 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Parental Settings</h1>
            <p className="text-gray-400">Manage safety and communication settings for your child's account</p>
          </div>

          <div className="space-y-6">
            {/* Account Monitoring */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <Eye className="h-6 w-6" />
                  Account Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="child-monitoring" className="text-white font-medium cursor-pointer">
                        Child Account Monitoring
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Monitor your child's account activity and interactions
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="child-monitoring"
                    checked={childAccountMonitoring}
                    onCheckedChange={setChildAccountMonitoring}
                  />
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Bell className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Activity Reports</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Receive regular reports about your child's platform activity
                  </p>
                  <Select value={activityReports} onValueChange={setActivityReports}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="daily">Daily Reports</SelectItem>
                      <SelectItem value="weekly">Weekly Reports</SelectItem>
                      <SelectItem value="monthly">Monthly Reports</SelectItem>
                      <SelectItem value="none">No Reports</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Communication Controls */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <Users className="h-6 w-6" />
                  Communication Controls
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="message-approval" className="text-white font-medium cursor-pointer">
                        Message Approval Required
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Require your approval for all incoming and outgoing messages
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="message-approval"
                    checked={messageApproval}
                    onCheckedChange={setMessageApproval}
                  />
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Team Communication</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Control how your child can communicate with team members
                  </p>
                  <Select value={teamCommunication} onValueChange={setTeamCommunication}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="unrestricted">Unrestricted</SelectItem>
                      <SelectItem value="approval-required">Approval Required</SelectItem>
                      <SelectItem value="coaches-only">Coaches Only</SelectItem>
                      <SelectItem value="no-communication">No Communication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="coach-communication" className="text-white font-medium cursor-pointer">
                        Direct Coach Communication
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Allow coaches to communicate directly with your child
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="coach-communication"
                    checked={coachCommunication}
                    onCheckedChange={setCoachCommunication}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Safety Settings */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  Safety Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="emergency-notifications" className="text-white font-medium cursor-pointer">
                        Emergency Notifications
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Receive immediate notifications for emergency situations
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="emergency-notifications"
                    checked={emergencyNotifications}
                    onCheckedChange={setEmergencyNotifications}
                  />
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="h-5 w-5 text-sports-green" />
                    <Label htmlFor="screen-time" className="text-white font-medium">Screen Time Limit (Hours)</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Set daily screen time limit for the platform (0 = no limit)
                  </p>
                  <Input
                    id="screen-time"
                    type="number"
                    min="0"
                    max="12"
                    value={screenTimeLimit}
                    onChange={(e) => setScreenTimeLimit(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white w-32"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <Settings className="h-6 w-6" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                    View Child's Activity
                  </Button>
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                    Review Pending Messages
                  </Button>
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                    Manage Connected Accounts
                  </Button>
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                    Download Activity Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Save Settings */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                className="bg-sports-green hover:bg-sports-green/90 text-black"
              >
                Save Parental Settings
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default ParentalSettings;
